import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { postRequest, getRequest } from '../../../lib/api';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AuthContext } from '../../../authContext/context';
import { API_BASE_URL, API_BASE } from '../../../lib/config';
import Swal from 'sweetalert2';
import Notes from '../Notes/note';
import jsPDF from 'jspdf';

// Revise Quote Modal Component for Review Legal Pack
function ReviseQuoteModal({ show, onHide, quoteFeeData, leadData, authToken, onReviseQuote }) {
  const [fees, setFees] = useState({
    legal_fee: 0
  });
  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0,
    legalCost: { net: 0, vat: 0, gross: 0 },
    disbursementCost: { net: 0, vat: 0, gross: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize with quote fee data
  React.useEffect(() => {
    if (show && quoteFeeData) {
      const qf = quoteFeeData.fees || {};
      const initialFees = {
        legal_fee: qf.legalFee?.amount || quoteFeeData.net || 0
      };
      setFees(initialFees);
      calculateTotals(initialFees);
    }
  }, [quoteFeeData, show]);

  // Calculate totals
  const calculateTotals = (currentFees) => {
    const legalFeeNet = Number(currentFees.legal_fee) || 0;
    const legalFeeVAT = legalFeeNet * 0.20;

    // Calculate Legal Cost (only legal fee for this service)
    const legalCostNet = legalFeeNet;
    const legalCostVAT = legalFeeVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    // No Disbursement Cost for this service
    const disbursementCostNet = 0;
    const disbursementCostVAT = 0;
    const disbursementCostGross = 0;

    // Calculate Grand Totals
    const totalNet = legalCostNet + disbursementCostNet;
    const totalVAT = legalCostVAT + disbursementCostVAT;
    const totalGross = totalNet + totalVAT;

    setTotals({
      net: totalNet,
      vat: totalVAT,
      gross: totalGross,
      legalCost: { net: legalCostNet, vat: legalCostVAT, gross: legalCostGross },
      disbursementCost: { net: disbursementCostNet, vat: disbursementCostVAT, gross: disbursementCostGross }
    });
  };

  const handleReviseQuote = async () => {
    try {
      setIsLoading(true);

      const payload = {
        lead_id: leadData.lead_id || leadData._id,
        legal_fee: Number(fees.legal_fee),
        vat_amount: totals.vat,
        total_quote: totals.gross,
        fees: {
          legal_fee: Number(fees.legal_fee)
        }
      };

      console.log('📝 Modal submitting revised quote for Review Legal Pack:', payload);

      // Call the onReviseQuote callback
      await onReviseQuote(payload);

      setIsLoading(false);
    } catch (err) {
      console.error('❌ Error in ReviseQuoteModal:', err);
      setIsLoading(false);
      Swal.fire('Error', err?.message || 'Failed to revise quote', 'error');
    }
  };

  const handleFeeChange = (field, value) => {
    const newFees = { ...fees, [field]: value };
    setFees(newFees);
    calculateTotals(newFees);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Revise Quote - Review of Legal Pack</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table bordered>
          <thead>
            <tr>
              <th>Description</th>
              <th style={{ width: '150px' }}>Net (£)</th>
              <th style={{ width: '150px' }}>VAT (£)</th>
              <th style={{ width: '150px' }}>Gross (£)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Legal Fee</strong></td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  value={fees.legal_fee}
                  onChange={(e) => handleFeeChange('legal_fee', e.target.value)}
                  step="0.01"
                  min="0"
                />
              </td>
              <td className="align-middle">
                £{(Number(fees.legal_fee) * 0.20).toFixed(2)}
              </td>
              <td className="align-middle">
                £{(Number(fees.legal_fee) * 1.20).toFixed(2)}
              </td>
            </tr>
            {/* TOTAL LEGAL COST */}
            <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
              <td>TOTAL LEGAL COST</td>
              <td>£{totals.legalCost.net.toFixed(2)}</td>
              <td>£{totals.legalCost.vat.toFixed(2)}</td>
              <td>£{totals.legalCost.gross.toFixed(2)}</td>
            </tr>

            {/* TOTAL DISBURSEMENT COST */}
            <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
              <td>TOTAL DISBURSEMENT COST</td>
              <td>£{totals.disbursementCost.net.toFixed(2)}</td>
              <td>£{totals.disbursementCost.vat.toFixed(2)}</td>
              <td>£{totals.disbursementCost.gross.toFixed(2)}</td>
            </tr>

            {/* Total (Legal cost + disbursement) */}
            <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', borderTop: '2px solid #28a745' }}>
              <td>Total (Legal cost + disbursement)</td>
              <td>£{totals.net.toFixed(2)}</td>
              <td>£{totals.vat.toFixed(2)}</td>
              <td>£{totals.gross.toFixed(2)}</td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outlined" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleReviseQuote} disabled={isLoading} style={{ paddingLeft: '25px', paddingRight: '25px', fontWeight: '500' }}>
          {isLoading ? 'Saving...' : 'Revise Quote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const ViewLeadReviewOfLegalPack = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFeeData, setQuoteFeeData] = useState(null);
  const [loadingFee, setLoadingFee] = useState(true); // ✅ Start with true to show loading initially
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [reviseModalShow, setReviseModalShow] = useState(false);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [revisionCount, setRevisionCount] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);

  // Communication / Email states
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [viewEmailModal, setViewEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contactAttempts, setContactAttempts] = useState(0);

  useEffect(() => {
    if (lead_id && authCtx.token) {
      fetchLeadData();
      fetchLenderPanels();
      fetchPartners();
    }
  }, [lead_id, authCtx.token]);

  // Fetch branch data when leadData is loaded
  useEffect(() => {
    if (leadData && authCtx.token) {
      fetchQuoteFee();

      // ⏰ Delay quote history fetch by 10 seconds to allow initial calculation to be created
      const historyTimer = setTimeout(() => {
        console.log('⏰ 10 seconds passed, fetching quote history now...');
        fetchQuoteHistory();
      }, 10000); // 10 seconds delay

      fetchEmailLogs();
      fetchEmailTemplates();
      setContactAttempts(leadData.contact_attempts || 0);

      // Cleanup timer on unmount
      return () => clearTimeout(historyTimer);
    }
  }, [leadData, authCtx.token]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching Review of Legal Pack lead data for:', lead_id);

      // Use get-single-lead API instead of lead-overview-data (same as edit form)
      const res = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);

      if ((res?.status === 200 || res?.status === 'success') && res?.data) {
        const data = res.data;
        setLeadData(data);
        setContactAttempts(data.contact_attempts || 0);
        setRevisionCount(data.revision_count || 0);
        console.log('✅ Lead data loaded:', data);
        console.log('🏠 Address:', data.address || data.property_address);
        console.log('👥 Customers:', data.customers);
        console.log('🏦 Lender Panel:', data.lender_panel_id);
        console.log('📊 Revision Count:', data.revision_count || 0);
      } else {
        console.log('⚠️ No lead data found');
      }
    } catch (err) {
      console.error('❌ Error fetching lead data:', err);
      Swal.fire('Error', 'Failed to load lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
        console.log('✅ Lender panels loaded:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching lender panels:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
        console.log('✅ Partners loaded:', response.data.docs);
      }
    } catch (error) {
      console.error('❌ Error fetching partners:', error);
    }
  };

  const fetchQuoteHistory = async () => {
    try {
      console.log('📚 Fetching quote history for lead:', lead_id);
      const apiUrl = `${API_BASE_URL}private/getQuoteHistory/${lead_id}`;

      const res = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        if (res.status === 404) {
          console.log('ℹ️ No quote history yet (new lead)');
          setQuoteHistory([]); // Set empty array for new leads
        } else {
          console.log('⚠️ Failed to fetch quote history');
        }
        return;
      }

      const result = await res.json();
      console.log('📚 Quote History Response:', result);

      if (result?.status === 200 && result?.data) {
        setQuoteHistory(result.data);
        console.log('✅ Quote history loaded:', result.data.length, 'entries');
      }
    } catch (err) {
      console.error('❌ Error fetching quote history:', err);
    }
  };

  const fetchQuoteFee = async () => {
    if (!leadData) return;

    try {
      setLoadingFee(true);
      console.log('🔍 Fetching Review of Legal Pack fee from database for:', lead_id);

      // 🎯 Try to fetch from database using fetch_lead_calculations API
      const partnerId = leadData.partner_id?._id || leadData.partner_id;
      const fetchUrl = partnerId
        ? `private/fetch_lead_calculations/${lead_id}?partner_id=${partnerId}`
        : `private/fetch_lead_calculations/${lead_id}`;

      console.log('📞 Fetching active quote from:', fetchUrl);

      const response = await getRequest(fetchUrl, authCtx.token);

      console.log('💰 fetch_lead_calculations Response:', response);

      if (response?.status === 200 && response?.data) {
        const data = response.data;

        console.log('✅ Active quote loaded from database:', data);

        // Set quoteFeeData for display and Revise Quote Modal
        setQuoteFeeData({
          fees: data.fees || {},
          disbursements: data.disbursements || {},
          totals: data.totals || {},
          net: data.totals?.total?.net || 0,
          vat: data.totals?.total?.vat || 0,
          gross: data.totals?.total?.gross || 0,
          branch_name: data.branch_name || '',
          ownership_type: leadData.ownership_type || 'freehold'
        });

        console.log('✅ quoteFeeData set successfully');
      } else if (response?.status === 404) {
        // No calculation exists yet, create initial one
        console.log('⚠️ No calculation found (404), creating initial calculation...');
        await createInitialCalculation();
      } else {
        console.log('⚠️ Unexpected response:', response);
      }

    } catch (err) {
      console.error('❌ Error fetching quote fee:', err);
      // On error, try to create initial calculation
      console.log('🔄 Attempting to create initial calculation...');
      try {
        await createInitialCalculation();
      } catch (createErr) {
        console.error('❌ Failed to create initial calculation:', createErr);
        Swal.fire('Error', 'Failed to load quote data', 'error');
      }
    } finally {
      setLoadingFee(false);
    }
  };

  // Create initial calculation if none exists
  const createInitialCalculation = async () => {
    try {
      console.log('🔨 Creating initial calculation...');

      const address = leadData.property_address || leadData.address;
      let postcode = '';

      if (typeof address === 'object') {
        postcode = address.postcode || '';
      } else if (typeof address === 'string') {
        const parts = address.split(',');
        postcode = parts[parts.length - 1]?.trim() || '';
      }

      if (!postcode) {
        console.log('⚠️ No postcode found');
        return;
      }

      const ownershipType = leadData.ownership_type || (leadData.leasehold ? 'leasehold' : 'freehold');
      const productsRes = await postRequest('private/getproductList', {}, authCtx.token);
      const conveyancingProduct = productsRes?.data?.find(product =>
        product.title?.toLowerCase().includes('conveyancing')
      );

      if (!conveyancingProduct) {
        console.log('❌ Conveyancing product not found');
        return;
      }

      const propertyValue = leadData?.propertyDetails?.propertyValue
        || leadData?.propertyDetails?.property_value
        || leadData?.property_value
        || 0;

      const requestBody = {
        lead_id: lead_id,
        service_type: 'Review_Legal_Pack',
        property_value: propertyValue,
        product_id: conveyancingProduct._id,
        ownership_type: ownershipType
      };

      const partnerId = typeof leadData.partner_id === 'object' ? leadData.partner_id?._id : leadData.partner_id;

      if (partnerId) {
        requestBody.partner_id = partnerId;
      } else {
        requestBody.postcode = postcode;
      }

      console.log('📤 Creating initial calculation:', requestBody);

      const response = await postRequest('private/calculateAndSaveLeadQuote', requestBody, authCtx.token);

      if (response?.status === 200 && response?.data) {
        console.log('✅ Initial calculation created:', response.data);

        // Set quoteFeeData directly from response (avoid recursive call)
        setQuoteFeeData({
          fees: response.data.fees || {},
          disbursements: response.data.disbursements || {},
          totals: response.data.totals || {},
          net: response.data.totals?.total?.net || 0,
          vat: response.data.totals?.total?.vat || 0,
          gross: response.data.totals?.total?.gross || 0,
          branch_name: response.data.branch_name || '',
          ownership_type: ownershipType
        });

        console.log('✅ quoteFeeData set from initial calculation');

        // 🎯 Quote history will be fetched by the 10-second delayed timer in useEffect
        // No need to fetch immediately here as the calculation is just being created
        console.log('ℹ️ Quote history will be fetched automatically after 10 seconds');
      }

    } catch (err) {
      console.error('❌ Error creating initial calculation:', err);
      throw err; // Re-throw so calling function knows it failed
    }
  };

  // Handle Revise Quote with NEW API
  const handleReviseQuote = async (payload) => {
    try {
      console.log('🔄 ===== REVISE QUOTE (NEW API) =====');
      console.log('📦 Received payload from modal:', payload);

      // Transform flat fees to NESTED format for Review Legal Pack
      // Review Legal Pack has a simpler fee structure (mainly legal fee)
      const nestedFees = {
        legalFee: {
          amount: Number(payload.fees.legal_fee || 0),
          vat: Number(payload.fees.legal_fee || 0) * 0.20,
          gross: Number(payload.fees.legal_fee || 0) * 1.20,
          label: 'Legal Fee'
        }
      };

      const nestedDisbursements = {};

      // Calculate totals
      const legalCostNet = nestedFees.legalFee.amount;
      const legalCostVat = nestedFees.legalFee.vat;
      const legalCostGross = nestedFees.legalFee.gross;

      const nestedTotals = {
        legalCost: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross },
        disbursementCost: { net: 0, vat: 0, gross: 0 },
        total: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross }
      };

      console.log('✅ Nested format created:', { nestedFees, nestedDisbursements, nestedTotals });

      // Call NEW reviseQuote API
      const apiUrl = `${API_BASE_URL}private/reviseQuote/${lead_id}`;
      console.log('📡 Calling NEW reviseQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        },
        body: JSON.stringify({
          fees: nestedFees,
          disbursements: nestedDisbursements,
          totals: nestedTotals
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Revise Quote API Response:', result);

      if (result?.status === 200) {
        console.log('✅ Quote revised successfully!');

        // Fetch fresh quote history from database
        await fetchQuoteHistory();

        // Refresh active quote
        await fetchQuoteFee();

        Swal.fire('Success', 'Quote revised successfully!', 'success');

        // Close the revise quote modal
        setReviseModalShow(false);
      } else {
        Swal.fire('Error', result?.message || 'Failed to revise quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error revising quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // Handle Regenerate Quote - Restore Original Quote
  const handleRegenerateQuote = async () => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Regenerate Original Quote?',
        text: 'This will restore the original quote values. Current changes will be saved to history.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate it!'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      console.log('🔁 ===== REGENERATE QUOTE =====');
      console.log('📋 Lead ID:', lead_id);

      // Call regenerateQuote API
      const apiUrl = `${API_BASE_URL}private/regenerateQuote/${lead_id}`;
      console.log('📡 Calling regenerateQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Regenerate Quote API Response:', result);

      if (result.status === 200) {
        // Refresh data
        await fetchQuoteHistory();
        await fetchQuoteFee();

        Swal.fire('Success', 'Original quote restored successfully!', 'success');
      } else {
        Swal.fire('Error', result?.message || 'Failed to regenerate quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error regenerating quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  const downloadQuotePDF = () => {
    if (!quoteFeeData) {
      Swal.fire('Error', 'No quote data available to download', 'error');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;

      // Top header - Quote # and Generated date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const currentTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      doc.text(`Quote #L-${lead_id}; Generated ${currentDate} ${currentTime}`, margin, yPosition);
      yPosition += 10;

      // Lead ID
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Lead ID: L-${lead_id}`, margin, yPosition);
      yPosition += 10;

      // Main Title - Quote for Review of Legal Pack
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Quote for Review of Legal Pack', margin, yPosition);
      yPosition += 15;

      // Description paragraph
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const description = `Thank you for requesting a conveyancing quote from My Law Matters, you will find a complete breakdown of our costs below. We would be delighted to act for you and one of our team will contact you shortly. In the meantime if you have any questions at all then please do get in touch on 0330 124 2171.`;
      const splitDescription = doc.splitTextToSize(description, pageWidth - (margin * 2));
      doc.text(splitDescription, margin, yPosition);
      yPosition += 25;

      // Section Header - REVIEW OF LEGAL PACK (in blue)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 128, 185); // Blue color
      doc.text('REVIEW OF LEGAL PACK', margin, yPosition);
      yPosition += 10;

      // Total Cost Summary
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Cost: £${quoteFeeData.gross.toFixed(2)} (£${quoteFeeData.net.toFixed(2)} + £${quoteFeeData.vat.toFixed(2)} VAT)`, margin, yPosition);
      yPosition += 15;

      // Manual Table Drawing
      const tableWidth = pageWidth - (margin * 2);
      const colWidth = tableWidth / 4; // 4 columns

      // Table header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(240, 240, 240); // Light gray background

      // Draw header row with background
      doc.rect(margin, yPosition, tableWidth, 8, 'FD');

      // Header text
      doc.text('Description', margin + 2, yPosition + 5);
      doc.text('Net', margin + colWidth + 2, yPosition + 5);
      doc.text('VAT', margin + (colWidth * 2) + 2, yPosition + 5);
      doc.text('Total', margin + (colWidth * 3) + 2, yPosition + 5);

      yPosition += 8;

      // Table data row
      doc.setFont('helvetica', 'normal');

      // Draw data row with border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      doc.rect(margin, yPosition, tableWidth, 8, 'S');

      // Data text
      doc.text(`Legal Fees`, margin + 2, yPosition + 5);
      doc.text(`£${quoteFeeData.net.toFixed(2)}`, margin + colWidth + 2, yPosition + 5);
      doc.text(`£${quoteFeeData.vat.toFixed(2)}`, margin + (colWidth * 2) + 2, yPosition + 5);
      doc.text(`£${quoteFeeData.gross.toFixed(2)}`, margin + (colWidth * 3) + 2, yPosition + 5);

      yPosition += 8;

      // TOTAL row
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);

      // Draw TOTAL row with light gray background
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPosition, colWidth, 8, 'FD');
      doc.text('TOTAL (Legal Cost)', margin + 2, yPosition + 5);

      doc.setFillColor(240, 240, 240);
      doc.rect(margin + colWidth, yPosition, colWidth, 8, 'FD');
      doc.text(`£${quoteFeeData.net.toFixed(2)}`, margin + colWidth + 2, yPosition + 5);

      doc.setFillColor(240, 240, 240);
      doc.rect(margin + (colWidth * 2), yPosition, colWidth, 8, 'FD');
      doc.text(`£${quoteFeeData.vat.toFixed(2)}`, margin + (colWidth * 2) + 2, yPosition + 5);

      doc.setFillColor(240, 240, 240);
      doc.rect(margin + (colWidth * 3), yPosition, colWidth, 8, 'FD');
      doc.text(`£${quoteFeeData.gross.toFixed(2)}`, margin + (colWidth * 3) + 2, yPosition + 5);

      // Save the PDF
      doc.save(`Review_of_Legal_Pack_Quote_L${lead_id}.pdf`);

      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      Swal.fire('Error', 'Failed to generate PDF', 'error');
    }
  };

  const handleDeleteLead = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this lead #${lead_id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log('Attempting to delete lead with ID:', lead_id);

        // Use the DELETE method with fetch API
        const response = await fetch(`/api/v1/admin/private/delete-lead/${lead_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Delete response status:', response.status);

        let res;
        if (response.ok) {
          res = await response.json();
          console.log('Delete response data:', res);
        } else {
          // If the specific delete endpoint fails, log the error
          const errorText = await response.text();
          console.error('Delete failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Delete failed with status ${response.status}`);
        }

        if (res && (res.status === 200 || res.status === 'success')) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'The lead has been deleted successfully.',
            timer: 1500,
            showConfirmButton: false
          });

          // Navigate back to leads list after a short delay
          setTimeout(() => {
            navigate('/leads');
          }, 1500);
        } else {
          throw new Error(res?.message || 'Failed to delete lead');
        }
      } catch (err) {
        console.error('Error deleting lead:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'An error occurred while deleting the lead.'
        });
      }
    }
  };

  // Fetch email logs/communication history
  const fetchEmailLogs = async () => {
    try {
      setLoadingEmails(true);
      console.log('📧 Fetching email logs for lead:', lead_id);

      const emailLogsResponse = await postRequest('private/getEmailLogs', {
        lead_id: lead_id,
        page: 1,
        limit: 100
      }, authCtx.token);

      console.log('📧 Email logs response:', emailLogsResponse);

      const sentEmails = (emailLogsResponse?.status === 200 || emailLogsResponse?.status === 'success')
        ? (emailLogsResponse.data || [])
        : [];

      setEmailLogs(sentEmails);
      console.log('✅ Email logs loaded:', sentEmails.length);

      setLoadingEmails(false);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
      setLoadingEmails(false);
    }
  };

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    try {
      console.log('📧 === FETCHING EMAIL TEMPLATES ===');
      console.log('Lead data product_id:', leadData?.product_id);

      // Get product ID from leadData
      let productId = leadData?.product_id?._id || leadData?.product_id;
      console.log('🔍 Product ID:', productId);

      // If productId is not available, try to fetch it from products API
      if (!productId) {
        console.log('⚠️ Product ID not found, fetching products...');
        try {
          const productsRes = await postRequest('private/getAllProducts', {}, authCtx.token);
          if (productsRes?.data) {
            const conveyancingProduct = productsRes.data.find(p =>
              p.name?.toLowerCase().includes('conveyancing') ||
              p.product_name?.toLowerCase().includes('conveyancing')
            );
            if (conveyancingProduct) {
              productId = conveyancingProduct._id || conveyancingProduct.product_id;
              console.log('✅ Found Conveyancing product ID:', productId);
            }
          }
        } catch (prodErr) {
          console.log('❌ Error fetching products:', prodErr);
        }
      }

      // Fetch automation actions for this product
      const automationUrl = `${API_BASE}/v1/admin/automationList`;
      const requestBody = {
        productId: productId,
        page: 1,
        limit: 100
      };

      const response = await fetch(automationUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      console.log('📡 Automations response:', responseData);

      if (response.ok && (responseData?.status === 200 || responseData?.status === 'success')) {
        const automations = responseData.data?.docs || responseData.data || [];
        console.log('✅ Automations found:', automations.length);

        // Extract email templates from automation actions
        const emailTemplates = [];

        automations.forEach((automation) => {
          // Check if automation has effect with "Send Email"
          if (automation.effect && automation.effect.type?.toLowerCase().includes('send email')) {
            console.log('   ✅ Found Send Email effect:', automation.effect.type);

            const template = {
              _id: automation.effect.emailTemplateId?._id || automation._id,
              name: automation.effect.emailTemplateId?.name || 'Unnamed Template',
              template_id: automation.effect.emailTemplateId?._id,
              automation_id: automation._id,
              effect: automation.effect
            };

            emailTemplates.push(template);
            console.log('   ✅ Added email template:', template.name);
          }
        });

        console.log('📧 Total email templates extracted:', emailTemplates.length);
        setEmailTemplates(emailTemplates);
      } else {
        console.log('⚠️ No automations found');
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  // Handle send email
  const handleSendEmail = async (templateId, templateName) => {
    try {
      setSendingEmail(true);

      const response = await postRequest('private/sendEmailFromTemplate', {
        lead_id: lead_id,
        template_id: templateId
      }, authCtx.token);

      console.log('Email send response:', response);

      if (response?.status === 200 || response?.status === 'success') {
        const successCount = response?.data?.successCount || 0;
        const recipients = successCount > 1 ? `${successCount} customers` : 'customer';

        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: `Email "${templateName}" has been sent successfully to ${recipients}!`
        });
        fetchEmailLogs();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Send Email',
          text: response?.message || response?.error || 'Failed to send email. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to send email. Please try again.'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle contact attempt update
  const handleUpdateContactAttempt = async (action) => {
    try {
      let newCount = contactAttempts;
      if (action === 'increase') {
        newCount = contactAttempts + 1;
      } else if (action === 'reset') {
        newCount = 0;
      }

      const response = await postRequest('private/updateContactAttempt', {
        lead_id: lead_id,
        contact_attempts: newCount
      }, authCtx.token);

      if (response?.status === 200 || response?.status === 'success') {
        setContactAttempts(newCount);
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: `Contact attempts ${action === 'reset' ? 'reset' : 'updated'} successfully!`,
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating contact attempts:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update contact attempts'
      });
    }
  };

  // Handle view email
  const handleViewEmail = (emailLog) => {
    setSelectedEmail(emailLog);
    setViewEmailModal(true);
  };

  const slugify = (text) =>
    text
      ?.toLowerCase()
      ?.replace(/ /g, '-')
      ?.replace(/[^\w-]+/g, '')
      ?.replace(/-+/g, '-');

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="text-center mt-5">
        <p>No lead data found</p>
      </div>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `Review of Legal Pack #${lead_id}`, path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3'>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className='mt-0 mb-2'>
              Conveyancing (Review of Legal Pack) #{leadData?.lead_id || lead_id}
            </h1>
          </div>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <div className='customer-tab-view leads-tab-view'>
          <Tabs defaultActiveKey="overview" id="review-legal-pack-tabs">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <div className='mt-3'>
                {/* Quote Summary Box - Purchase Style */}
                <div className="card shadow-sm mb-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <div className="card-header" style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    padding: '1rem 1.25rem'
                  }}>
                    <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                      Quote Summary
                    </h5>
                  </div>
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-baseline gap-3">
                        <span className="text-muted small">Total Cost</span>
                        {loadingFee ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            Loading...
                          </span>
                        ) : quoteFeeData ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{quoteFeeData.net.toFixed(2)} Net + £{quoteFeeData.vat.toFixed(2)} VAT = £{quoteFeeData.gross.toFixed(2)} Gross
                          </span>
                        ) : (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{((leadData?.net_value || leadData?.legal_fee || 0)).toFixed(2)} Net + £{((leadData?.vat_amount || 0)).toFixed(2)} VAT = £{(leadData?.gross_value || leadData?.total_quote || 0).toFixed(2)} Gross
                          </span>
                        )}
                      </div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowQuoteModal(true);
                          fetchQuoteFee();
                        }}
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Quote Detail
                      </a>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-secondary me-2">
                          <i className="fas fa-building me-1"></i>
                          {leadData?.branch_name || 'Manchester'}
                        </span>
                        <span className="badge bg-info">{leadData?.created_by || localStorage.getItem('username') || 'User'}</span>
                      </div>
                      <div className="text-muted small">
                        {(() => {
                          const date = leadData?.quote_date || leadData?.createdAt || leadData?.created_at;
                          if (!date) return 'Date not available';
                          const dateObj = new Date(date);
                          return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="card-footer bg-light" style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      {leadData?.status !== 'Instructed' && (
                        <Button
                          variant="contained"
                          className='text-capitalize'
                          onClick={async () => {
                            // Fetch quote data first if not already available
                            if (!quoteFeeData) {
                              console.log('🔄 Fetching quote data for Revise Quote...');
                              await fetchQuoteFee();
                            }
                            setReviseModalShow(true);
                          }}
                          disabled={revisionCount >= 2 || loadingFee}
                          style={{
                            backgroundColor: revisionCount >= 2 ? '#6c757d' : '#dc3545',
                            borderColor: revisionCount >= 2 ? '#6c757d' : '#dc3545',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" style={{ marginRight: '6px' }} />
                          {loadingFee ? 'Loading...' : revisionCount >= 2 ? 'Revision Limit Reached' : 'Revise Quote'}
                        </Button>
                      )}
                      {leadData?.status !== 'Instructed' && (
                        <Button
                          variant="outlined"
                          className='text-capitalize'
                          onClick={handleRegenerateQuote}
                          style={{
                            color: '#6c757d',
                            borderColor: '#6c757d',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <RefreshIcon fontSize="small" style={{ marginRight: '6px' }} />
                          Regenerate Quote
                        </Button>
                      )}
                      {revisionCount > 0 && (
                        <Button
                          variant="text"
                          className='text-capitalize'
                          onClick={() => {
                            // Scroll to Quote History section
                            const historySection = document.querySelector('.quote-history-section');
                            if (historySection) {
                              historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          style={{
                            color: '#6c757d',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <i className="fas fa-history me-1"></i>
                          View History ({revisionCount})
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <ul className="list-group mb-3">
                  {/* Address */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Address</div>
                      <div className="col-8">
                        {(() => {
                          // Try multiple possible locations for address data
                          const address = leadData?.property_address
                            || leadData?.address
                            || leadData?.conveyancing_details?.property_address
                            || leadData?.sale_property_address
                            || leadData?.purchase_property_address;

                          console.log('🏠 Address data:', address);
                          console.log('🏠 Full leadData:', leadData);

                          if (!address) return 'No address specified';

                          // If address is a string
                          if (typeof address === 'string') {
                            return address;
                          }

                          // If address is an object
                          if (typeof address === 'object') {
                            const parts = [];
                            if (address.line1 || address.address1) parts.push(address.line1 || address.address1);
                            if (address.line2 || address.address2) parts.push(address.line2 || address.address2);
                            if (address.town) parts.push(address.town);
                            if (address.county) parts.push(address.county);
                            if (address.postcode) parts.push(address.postcode);
                            return parts.length > 0 ? parts.join(', ') : 'No address specified';
                          }

                          return 'No address specified';
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Customers */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Customers</div>
                      <div className="col-8">
                        {(() => {
                          const customers = leadData?.customers;
                          if (!customers || customers.length === 0) return 'No customers';

                          // Check if customers is a string (customer names concatenated)
                          if (typeof customers === 'string') {
                            return customers;
                          }

                          // Check if customers is an array
                          if (Array.isArray(customers)) {
                            return customers.map((customer, index) => {
                              const customerData = typeof customer === 'object' ? customer : {};
                              const name = `${customerData?.title || ''} ${customerData?.forename || ''} ${customerData?.surname || ''}`.trim() || 'Unknown';
                              return (
                                <div key={index}>
                                  {name}
                                </div>
                              );
                            });
                          }

                          return 'No customers';
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Ownership Type */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Ownership Type</div>
                      <div className="col-8">
                        {leadData?.ownership_type || 'Freehold'}
                      </div>
                    </div>
                  </li>

                  {/* Lender Panel */}
                  {leadData?.lender_panel_id && (
                    <li className="list-group-item">
                      <div className="row">
                        <div className="col-4 fw-bold">Lender Panel</div>
                        <div className="col-8">
                          {(() => {
                            const lenderPanelId = leadData?.lender_panel_id;

                            // If lender panel is already populated as an object
                            if (lenderPanelId && typeof lenderPanelId === 'object' && lenderPanelId.name) {
                              return (
                                <div>
                                  <div style={{ color: '#495057' }}>
                                    {lenderPanelId.name}
                                  </div>
                                  {lenderPanelId?.lender_numbers && lenderPanelId.lender_numbers.length > 0 && (
                                    <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                      {lenderPanelId.lender_numbers.join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // If lender panel is an ID, find it in the lenderPanels array
                            if (lenderPanelId && typeof lenderPanelId === 'string') {
                              const matchedPanel = lenderPanels.find(panel => panel._id === lenderPanelId);
                              if (matchedPanel) {
                                return (
                                  <div>
                                    <div style={{ color: '#495057' }}>
                                      {matchedPanel.name}
                                    </div>
                                    {matchedPanel?.lender_numbers && matchedPanel.lender_numbers.length > 0 && (
                                      <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                        {matchedPanel.lender_numbers.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            }

                            // If no lender panel found
                            return <span className="text-muted">Not specified</span>;
                          })()}
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Partner */}
                  {leadData?.partner_id && (
                    <li className="list-group-item">
                      <div className="row">
                        <div className="col-4 fw-bold">Partner</div>
                        <div className="col-8">
                          {(() => {
                            const partnerId = leadData?.partner_id;

                            // If partner is already populated as an object
                            if (partnerId && typeof partnerId === 'object' && partnerId.name) {
                              return partnerId.name;
                            }

                            // If partner is an ID, find it in the partners array
                            if (partnerId && typeof partnerId === 'string') {
                              const matchedPartner = partners.find(partner => partner._id === partnerId);
                              if (matchedPartner) {
                                return matchedPartner.name;
                              }
                            }

                            // If no partner found
                            return <span className="text-muted">Not specified</span>;
                          })()}
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Group Code */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Group Code</div>
                      <div className="col-8">
                        {(() => {
                          const groupCode = leadData?.group_code;

                          // If group code is populated as an object
                          if (groupCode && typeof groupCode === 'object') {
                            return (
                              <span>
                                {groupCode.name} ({groupCode.code})
                              </span>
                            );
                          }

                          // If just code string exists
                          if (leadData?.group_code_code) {
                            return leadData.group_code_code;
                          }

                          return <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Status */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Status</div>
                      <div className="col-8">
                        {leadData?.status || leadData?.lead_status || 'Pending'}
                      </div>
                    </div>
                  </li>
                </ul>

                {/* Edit Button and More Actions */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        console.log('Edit button clicked - Review of Legal Pack');

                        // Get first customer ID if available - prioritize customer_id over _id
                        const firstCustomerId = leadData?.customers && Array.isArray(leadData.customers) && leadData.customers.length > 0
                          ? (leadData.customers[0].customer_id || leadData.customers[0]._id || leadData.customers[0])
                          : null;
                        const customerParam = firstCustomerId ? `?customerId=${firstCustomerId}` : '';

                        navigate(`/conveyancing/review-of-legal-pack/${leadData?.lead_id || lead_id}${customerParam}`);
                      }}
                      style={{
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" style={{ marginRight: '4px' }} />
                      Edit
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      Created: {leadData?.createdAt ? new Date(leadData.createdAt).toLocaleDateString('en-GB') : 'Date not available'} - {leadData?.created_by || localStorage.getItem('username') || 'Unknown'}
                    </span>

                    <Dropdown drop="up">
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          borderColor: '#6c757d',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}
                      >
                        More Actions
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changestatus', {
                              state: {
                                leadId: lead_id,
                                currentStatus: leadData?.status,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Status
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changegroupcode', {
                              state: {
                                id: lead_id,
                                title: slugify(leadData?.product_title),
                                returnUrl: `/leads/view/review-of-legal-pack/${lead_id}`
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Group Code
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handleDeleteLead} style={{ color: '#dc3545' }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Delete Lead
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </Tab>

            {/* QUOTES TAB */}
            <Tab eventKey="quotes" title="Quotes">
              <div className="mt-3">
                {leadData && (
                  <div className="card">
                    <div className="card-body">
                      <h5 className="mb-3">Current Quote</h5>
                      <div className="row">
                        <div className="col-md-3">
                          <small className="text-muted">Quote Date</small>
                          <p>{leadData?.quote_date ? new Date(leadData.quote_date).toLocaleDateString('en-GB') : 'N/A'}</p>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted">Legal Fee</small>
                          <p>£{(quoteFeeData?.net || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted">VAT</small>
                          <p>£{(quoteFeeData?.vat || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted">Total</small>
                          <p className="fw-bold">£{(quoteFeeData?.gross || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!quoteFeeData?.gross && (
                  <div className="text-center py-5">
                    <h5 className="text-muted">No Quotes Available</h5>
                    <p className="text-muted">No quotes have been generated yet.</p>
                  </div>
                )}

                {/* Quote History Section - Enhanced UI */}
                {quoteHistory && quoteHistory.length > 0 && (
                  <div className="quote-history-section mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                        <i className="fas fa-history me-2" style={{ color: '#3498db' }}></i>
                        Quote History
                      </h5>
                      <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                        {quoteHistory.length} {quoteHistory.length === 1 ? 'Version' : 'Versions'}
                      </span>
                    </div>

                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                      {/* Timeline line */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '20px',
                        bottom: '20px',
                        width: '2px',
                        backgroundColor: '#e0e0e0'
                      }}></div>

                      {quoteHistory.map((quote, index) => {
                        const totalGross = quote.totals?.total?.gross || 0;
                        const totalNet = quote.totals?.total?.net || 0;
                        const totalVat = quote.totals?.total?.vat || 0;
                        const legalCostGross = quote.totals?.legalCost?.gross || 0;
                        const isActive = quote.is_active || false;
                        const revisionNumber = quote.revision_number || 1;
                        const source = quote.calculation_source || 'api';

                        const versionLabel = quote.revision_label || `Revision ${revisionNumber}`;
                        const displayDate = quote.revised_at || quote.createdAt;

                        // Icon and color based on type
                        let iconClass = 'fa-file-invoice';
                        let iconColor = '#95a5a6';
                        let borderColor = '#e0e0e0';

                        if (isActive) {
                          iconClass = 'fa-check-circle';
                          iconColor = '#27ae60';
                          borderColor = '#27ae60';
                        } else if (versionLabel === 'Original') {
                          iconClass = 'fa-star';
                          iconColor = '#f39c12';
                        } else if (source === 'manual_revision') {
                          iconClass = 'fa-edit';
                          iconColor = '#3498db';
                        }

                        return (
                          <div key={quote._id || index} style={{ position: 'relative', marginBottom: '24px' }}>
                            {/* Timeline dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-26px',
                              top: '16px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: iconColor,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px ' + iconColor,
                              zIndex: 1
                            }}></div>

                            {/* Card */}
                            <div className="card" style={{
                              border: `2px solid ${isActive ? borderColor : '#e9ecef'}`,
                              borderRadius: '12px',
                              boxShadow: isActive ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease',
                              backgroundColor: isActive ? '#f8fff9' : 'white'
                            }}>
                              <div className="card-body" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  {/* Left: Version & Amount */}
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '18px' }}></i>
                                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                                        {versionLabel}
                                      </h5>
                                      {isActive && (
                                        <span className="badge" style={{
                                          backgroundColor: '#27ae60',
                                          color: 'white',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          CURRENT
                                        </span>
                                      )}
                                    </div>

                                    {/* Date & Time */}
                                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="far fa-clock me-1"></i>
                                      {new Date(displayDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })} at {new Date(displayDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>

                                    {/* Source Badge */}
                                    <span className="badge" style={{
                                      backgroundColor: source === 'manual_revision' ? '#3498db' : '#95a5a6',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem'
                                    }}>
                                      {source === 'manual_revision' ? 'MANUAL REVISION' :
                                       source === 'api' ? 'AUTO CALCULATED' :
                                       source.toUpperCase()}
                                    </span>
                                  </div>

                                  {/* Right: Branch & Action */}
                                  <div className="d-flex flex-column align-items-end gap-2">
                                    {quote.branch_name && (
                                      <span className="badge" style={{
                                        backgroundColor: '#e8f4f8',
                                        color: '#3498db',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        <i className="fas fa-building me-1"></i>
                                        {quote.branch_name}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        borderWidth: '2px'
                                      }}
                                      onClick={() => {
                                        console.log('🔍 View Quote clicked for:', versionLabel);

                                        // Transform quote data for Review of Legal Pack (only legal fee)
                                        const transformedFeeData = {
                                          branch_name: quote.branch_name,
                                          service_type: quote.service_type,
                                          legal_fee: quote.fees?.legalFee?.amount || 0,
                                          totals: quote.totals,
                                          all_fees: quote.fees,
                                          all_disbursements: quote.disbursements
                                        };

                                        setSelectedQuote({
                                          legalFeeData: transformedFeeData,
                                          leadData: leadData,
                                          revision_label: versionLabel,
                                          is_history: !isActive
                                        });
                                        setShowQuoteDetailModal(true);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" style={{ fontSize: '14px', marginRight: '4px' }} />
                                      View Details
                                    </Button>
                                  </div>
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                  <div className="row g-3">
                                    {/* Total Amount - Highlighted */}
                                    <div className="col-md-4">
                                      <div style={{
                                        backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                      }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                          Total Amount
                                        </p>
                                        <h4 className="mb-0" style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700 }}>
                                          £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h4>
                                      </div>
                                    </div>

                                    {/* Legal Costs */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Legal Costs</p>
                                        <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#2c3e50' }}>
                                          £{Number(legalCostGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Net + VAT */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          Net: £{Number(totalNet).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                                          VAT: £{Number(totalVat).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Tab>

            {/* NOTES TAB */}
            <Tab eventKey="notes" title="Notes">
              <Notes lead_id={lead_id} product_id={leadData?.product_id} />
            </Tab>

            {/* COMMUNICATION TAB */}
            <Tab eventKey="communication" title="Communication">
              <div className='communication-table'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Contact Attempt ({contactAttempts})
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('increase')}>
                        <MailOutlineIcon fontSize="small" /> &nbsp; Increase Contact Attempt Count
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('reset')}>
                        <CallOutlinedIcon fontSize="small" /> &nbsp; Reset
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Send Email Dropdown - Right Aligned */}
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="send-email-dropdown" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {emailTemplates.length === 0 ? (
                          <Dropdown.Item disabled>No templates available</Dropdown.Item>
                        ) : (
                          emailTemplates.map((template) => (
                            <Dropdown.Item
                              key={template._id}
                              onClick={() => handleSendEmail(template._id, template.name)}
                            >
                              {template.name}
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Refresh Button */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        fetchEmailTemplates();
                        fetchEmailLogs();
                      }}
                      title="Refresh templates and logs"
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </div>
                </div>

                {/* Email History Table */}
                <div className="mt-4">
                  <Table striped responsive bordered>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th>Customer</th>
                        <th>Content</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingEmails ? (
                        <tr>
                          <td colSpan="4" className="text-center">Loading...</td>
                        </tr>
                      ) : emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">No communication logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log, index) => (
                          <tr key={log._id || index}>
                            <td>
                              {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : (log.isAutomation ? <span className="text-muted">Not triggered</span> : 'N/A')}
                            </td>
                            <td>
                              <div>
                                {log.recipient_name || log.customer_id?.forename || 'Unknown'}
                                <div className="text-muted small">{log.recipient_email}</div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.template_name || log.subject || 'No title'}</strong>
                                <div className="text-muted small">
                                  {log.subject && log.subject !== log.template_name && (
                                    <span>Subject: {log.subject}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewEmail(log)}
                                title="View email content"
                                sx={{ minWidth: '40px', padding: '4px 8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                <div className="text-center py-5">
                  <h5 className="text-muted">Tasks Functionality Coming Soon</h5>
                  <p className="text-muted">Task management features will be available here.</p>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      {/* View Quote Modal */}
      <Modal show={showQuoteModal} onHide={() => setShowQuoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Quote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFee ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading quote details...</p>
            </div>
          ) : quoteFeeData ? (
            <>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Review of Legal Pack</th>
                    <th>Net</th>
                    <th>VAT (20%)</th>
                    <th>Gross</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Legal Fees ({quoteFeeData.ownership_type})</td>
                    <td>£{quoteFeeData.net.toFixed(2)}</td>
                    <td>£{quoteFeeData.vat.toFixed(2)}</td>
                    <td>£{quoteFeeData.gross.toFixed(2)}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan="3" className="text-end">Total:</td>
                    <td>£{quoteFeeData.gross.toFixed(2)}</td>
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No quote data available</p>
            </div>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outlined"
              size="small"
              onClick={downloadQuotePDF}
              disabled={!quoteFeeData}
              style={{
                borderColor: '#6c757d',
                color: '#6c757d',
                textTransform: 'none'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Download
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* View Email Modal */}
      <Modal show={viewEmailModal} onHide={() => setViewEmailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail ? (
            <div>
              <div
                dangerouslySetInnerHTML={{ __html: selectedEmail.content || 'No content available' }}
                style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No email selected</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outlined" onClick={() => setViewEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revise Quote Modal */}
      <ReviseQuoteModal
        show={reviseModalShow}
        onHide={() => setReviseModalShow(false)}
        quoteFeeData={quoteFeeData}
        leadData={leadData}
        authToken={authCtx.token}
        onReviseQuote={handleReviseQuote}
      />

      {/* View Quote Detail Modal */}
      {selectedQuote && (
        <Modal
          show={showQuoteDetailModal}
          onHide={() => setShowQuoteDetailModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>View Quote</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="table-responsive">
              <Table bordered>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Description</th>
                    <th className="text-end">Net</th>
                    <th className="text-end">VAT</th>
                    <th className="text-end">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Legal Cost Section */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Legal Cost</strong></td>
                  </tr>

                  {/* Legal Fee */}
                  <tr>
                    <td>Legal Fee</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.legal_fee || 0).toFixed(2)}</td>
                    <td className="text-end">£{((selectedQuote.legalFeeData?.legal_fee || 0) * 0.20).toFixed(2)}</td>
                    <td className="text-end">£{((selectedQuote.legalFeeData?.legal_fee || 0) * 1.20).toFixed(2)}</td>
                  </tr>

                  {/* TOTAL LEGAL COST */}
                  <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                    <td>TOTAL LEGAL COST</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.legalCost?.net || 0).toFixed(2)}</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.legalCost?.vat || 0).toFixed(2)}</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.legalCost?.gross || 0).toFixed(2)}</td>
                  </tr>

                  {/* TOTAL DISBURSEMENT COST */}
                  <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
                    <td>TOTAL DISBURSEMENT COST</td>
                    <td className="text-end">£0.00</td>
                    <td className="text-end">£0.00</td>
                    <td className="text-end">£0.00</td>
                  </tr>

                  {/* Total (Legal cost + disbursement) */}
                  <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', borderTop: '2px solid #28a745' }}>
                    <td>Total (Legal cost + disbursement)</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.total?.net || 0).toFixed(2)}</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.total?.vat || 0).toFixed(2)}</td>
                    <td className="text-end">£{(selectedQuote.legalFeeData?.totals?.total?.gross || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuoteDetailModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ViewLeadReviewOfLegalPack;
